import { useEffect, useState } from "react";
import Prism from "prismjs";

/**
 * Prism setup
 *
 * By default, `prism-react-renderer` bundles its own isolated Prism copy,
 * which makes it tricky to add extra languages (like R, etc.).
 *
 * Instead of mutating `window.Prism` or dealing with async dynamic imports,
 * we create our own Prism instance here, import the language definitions we need,
 * and then explicitly pass this Prism instance into `<Highlight prism={Prism} />`.
 *
 * This guarantees:
 *   - Only one Prism instance is used across the app.
 *   - Languages are registered before rendering (no race conditions).
 *   - Vite bundling doesn’t break, since all imports are explicit.
 */

/**
 * Ensure window.Prism exists before importing custom languages
 * see: https://github.com/FormidableLabs/prism-react-renderer?tab=readme-ov-file#custom-language-support
 */
(typeof global !== "undefined" ? global : window).Prism = Prism;

const SUPPORTED_LANGUAGES = ["r", "sas", "python", "sql", "bash", "json", "yaml", "log"] as const;

export type PrismLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const languageImportMap: Record<PrismLanguage, () => Promise<any>> = {
  r: () => import("prismjs/components/prism-r.min.js"),
  sas: () => import("prismjs/components/prism-sas.min.js"),
  python: () => import("prismjs/components/prism-python.min.js"),
  sql: () => import("prismjs/components/prism-sql.min.js"),
  bash: () => import("prismjs/components/prism-bash.min.js"),
  json: () => import("prismjs/components/prism-json.min.js"),
  yaml: () => import("prismjs/components/prism-yaml.min.js"),
  log: () => import("prismjs/components/prism-log.min.js"),
};

const loadedLanguages = new Set<PrismLanguage>();

/**
 * Hook to lazily load multiple PrismJS languages.
 * All are loaded by default if no language subset is passed to hook.
 *
 * @param languages - Optional override array of PrismJS languages to load.
 */
const usePrismLanguages = (languages?: PrismLanguage[]) => {
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const loadLanguages = async () => {
      const promises = (languages ?? SUPPORTED_LANGUAGES).map(async (lang) => {
        if (!loadedLanguages.has(lang)) {
          await languageImportMap[lang]();
          loadedLanguages.add(lang);
        }
      });

      await Promise.allSettled(promises);
      setIsLoading(false);
    };

    loadLanguages();
  }, [languages]);

  return { isLoading, Prism };
};

export default usePrismLanguages;
